import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Search, Loader2, CheckCircle2, AlertCircle, Receipt, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ThirdPartyClaimForm = ({ user, token, onClaimSubmitted, initialSerial = "" }) => {
  const navigate = useNavigate();
  const [serialNumber, setSerialNumber] = useState("");
  const [marketplace, setMarketplace] = useState("");
  const [marketplaces, setMarketplaces] = useState([]);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/claims/marketplaces`)
      .then((res) => setMarketplaces(res.data))
      .catch(() => setMarketplaces(["eBay", "Amazon", "Etsy", "Direct from BLKPacks", "Other"]));
  }, []);

  // Prefill from the top claim form when the user typed a slab sticker serial there
  useEffect(() => {
    if (initialSerial) {
      setSerialNumber(initialSerial);
      setCheckResult(null);
    }
  }, [initialSerial]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const checkSerial = async () => {
    if (!serialNumber.trim()) return;
    if (!token) { navigate("/login"); return; }
    setChecking(true);
    setCheckResult(null);
    try {
      const fd = new FormData();
      fd.append("serial_number", serialNumber);
      const res = await axios.post(`${API}/claims/check-serial`, fd, authHeaders);
      setCheckResult(res.data);
    } catch (err) {
      setCheckResult({ available: false, message: err.response?.data?.detail || "Error checking serial" });
    }
    setChecking(false);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setReceiptPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { navigate("/login"); return; }
    if (!serialNumber.trim()) { toast.error("Enter the serial number from your card / slab"); return; }
    if (!marketplace) { toast.error("Select where you purchased it"); return; }
    if (!receiptFile) { toast.error("Upload a receipt or proof of purchase"); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", receiptFile);
      const up = await axios.post(`${API}/claims/upload-receipt`, fd, authHeaders);
      const claimRes = await axios.post(`${API}/claims/submit`, {
        serial_number: serialNumber,
        marketplace,
        receipt_image_url: up.data.url,
        additional_notes: notes || null,
      }, authHeaders);
      toast.success(claimRes.data.message || "Claim submitted for review!");
      setSerialNumber(""); setMarketplace(""); setReceiptFile(null);
      setReceiptPreview(null); setNotes(""); setCheckResult(null);
      onClaimSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit claim. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div id="third-party-claim" className="journey-glow bg-[#0a0a12] rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto mt-8 scroll-mt-24" data-testid="third-party-claim-form">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 brand-gradient-bg rounded-xl flex items-center justify-center shadow-[0_0_20px_-5px_rgba(249,115,22,0.55)]">
          <Receipt className="text-black" size={20} strokeWidth={2.5} />
        </div>
        <div>
          <h2 className="text-white font-black text-lg">Bought from <span className="brand-gradient-text">eBay, Amazon, or a 3rd Party?</span></h2>
          <p className="text-gray-500 text-sm">Claim your digital moment — enter the serial from the slab, tell us where you bought it, and attach your receipt.</p>
        </div>
      </div>

      <div className="mt-4 bg-amber-500/[0.06] border border-amber-500/25 rounded-xl p-3.5 text-xs text-white/75 space-y-1.5" data-testid="tp-claim-genuine-notice">
        <p className="text-amber-300 font-black uppercase tracking-wider text-[11px]">Read before you submit</p>
        <p><strong className="text-white">Enter the GENUINE serial number on the sticker on the slab.</strong> Every BLK PACKS card sold has one on the back of the slab — it's mandatory for your claim.</p>
        <p>After you submit, <strong className="text-white">BLK PACKS support reviews and approves your claim</strong> — not the seller. Third-party sellers (eBay, Vinted, Whatnot, Amazon, etc.) have nothing to do with this platform or your submission.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 mt-5">
        {/* Serial + check */}
        <div>
          <label className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1.5 block">Genuine Slab Serial Number <span className="text-amber-400">(required)</span></label>
          <div className="flex gap-2">
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => { setSerialNumber(e.target.value); setCheckResult(null); }}
              placeholder="e.g. 000123"
              className="flex-1 bg-black/60 border border-white/10 focus:border-orange-500/60 transition-colors rounded-lg px-4 py-3 text-white placeholder-white/30 text-sm outline-none font-mono"
              data-testid="tp-claim-serial-input"
            />
            <button
              type="button"
              onClick={checkSerial}
              disabled={checking || !serialNumber.trim()}
              className="px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs font-bold uppercase tracking-wider hover:border-orange-500/50 disabled:opacity-40 flex items-center gap-1.5"
              data-testid="tp-claim-check-btn"
            >
              {checking ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check
            </button>
          </div>
          {checkResult && (
            <div className={`mt-2 text-xs flex items-start gap-1.5 ${checkResult.available ? (checkResult.found_in_vault ? "text-green-400" : "text-amber-300") : "text-red-400"}`} data-testid="tp-claim-check-result">
              {checkResult.available ? <CheckCircle2 size={13} className="mt-[1px] flex-shrink-0" /> : <AlertCircle size={13} className="mt-[1px] flex-shrink-0" />}
              <span>{checkResult.message}{checkResult.collectible ? ` — ${checkResult.collectible.name} (${checkResult.collectible.serial})` : ""}</span>
            </div>
          )}
        </div>

        {/* Marketplace */}
        <div>
          <label className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1.5 block">Where did you buy it?</label>
          <select
            value={marketplace}
            onChange={(e) => setMarketplace(e.target.value)}
            className="w-full bg-black/60 border border-white/10 focus:border-orange-500/60 rounded-lg px-4 py-3 text-white text-sm outline-none"
            data-testid="tp-claim-marketplace-select"
          >
            <option value="">Select marketplace…</option>
            {marketplaces.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Receipt upload */}
        <div>
          <label className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1.5 block">Receipt / Proof of Purchase</label>
          <label className="flex items-center justify-center gap-2 bg-black/60 border border-dashed border-white/15 hover:border-orange-500/50 rounded-lg px-4 py-5 cursor-pointer transition-colors" data-testid="tp-claim-receipt-drop">
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="tp-claim-receipt-input" />
            {receiptPreview ? (
              <img src={receiptPreview} alt="Receipt preview" className="h-24 rounded-lg object-cover" />
            ) : (
              <span className="flex items-center gap-2 text-gray-500 text-sm"><Upload size={16} /> Tap to upload a photo or screenshot</span>
            )}
          </label>
        </div>

        {/* Notes */}
        <div>
          <label className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1.5 block">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Order number, seller name, anything that helps us verify faster…"
            className="w-full bg-black/60 border border-white/10 focus:border-orange-500/60 rounded-lg px-4 py-3 text-white placeholder-white/30 text-sm outline-none resize-none"
            data-testid="tp-claim-notes-input"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full brand-gradient-btn font-black px-6 py-3.5 rounded-lg text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="tp-claim-submit-btn"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} strokeWidth={2.5} />}
          {submitting ? "Submitting…" : user ? "Submit Claim for Review" : "Sign in to Submit Claim"}
        </button>
        <p className="text-gray-600 text-[11px] text-center">Our team reviews claims within 24–48 hours. You'll get a notification and email once processed.</p>
      </form>
    </div>
  );
};

export default ThirdPartyClaimForm;
